import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // UI state for action modals
  activeModals: {},
  
  // Loading states for async operations
  loadingStates: {},
  
  // Selected items for bulk operations
  selectedItems: {},
  
  // Pickup code verification
  pickupVerification: {
    isVerifying: false,
    error: null,
  },
  
  // Return request states
  returnRequests: {
    selectedItems: {},
    reason: '',
    comments: '',
  },
  
  // Tracking information
  tracking: {
    isLoading: false,
    error: null,
    data: null,
  },
};

const fulfillmentGroupSlice = createSlice({
  name: 'fulfillmentGroup',
  initialState,
  reducers: {
    // Modal management
    openModal: (state, action) => {
      const { modalId, modalData } = action.payload;
      state.activeModals[modalId] = {
        isOpen: true,
        ...modalData,
      };
    },
    
    closeModal: (state, action) => {
      const { modalId } = action.payload;
      if (state.activeModals[modalId]) {
        state.activeModals[modalId].isOpen = false;
      }
    },
    
    clearModal: (state, action) => {
      const { modalId } = action.payload;
      delete state.activeModals[modalId];
    },
    
    clearAllModals: (state) => {
      state.activeModals = {};
    },
    
    // Loading state management
    setLoadingState: (state, action) => {
      const { key, isLoading } = action.payload;
      state.loadingStates[key] = isLoading;
    },
    
    clearLoadingState: (state, action) => {
      const { key } = action.payload;
      delete state.loadingStates[key];
    },
    
    // Item selection for bulk operations
    toggleItemSelection: (state, action) => {
      const { fulfillmentGroupId, itemId } = action.payload;
      if (!state.selectedItems[fulfillmentGroupId]) {
        state.selectedItems[fulfillmentGroupId] = {};
      }
      
      const isSelected = state.selectedItems[fulfillmentGroupId][itemId];
      if (isSelected) {
        delete state.selectedItems[fulfillmentGroupId][itemId];
      } else {
        state.selectedItems[fulfillmentGroupId][itemId] = true;
      }
    },
    
    selectAllItems: (state, action) => {
      const { fulfillmentGroupId, itemIds } = action.payload;
      state.selectedItems[fulfillmentGroupId] = {};
      itemIds.forEach(itemId => {
        state.selectedItems[fulfillmentGroupId][itemId] = true;
      });
    },
    
    clearItemSelection: (state, action) => {
      const { fulfillmentGroupId } = action.payload;
      if (fulfillmentGroupId) {
        delete state.selectedItems[fulfillmentGroupId];
      } else {
        state.selectedItems = {};
      }
    },
    
    // Pickup verification
    setPickupVerifying: (state, action) => {
      state.pickupVerification.isVerifying = action.payload;
      if (action.payload) {
        state.pickupVerification.error = null;
      }
    },
    
    setPickupError: (state, action) => {
      state.pickupVerification.error = action.payload;
      state.pickupVerification.isVerifying = false;
    },
    
    clearPickupError: (state) => {
      state.pickupVerification.error = null;
    },
    
    // Return request management
    updateReturnRequest: (state, action) => {
      const { fulfillmentGroupId, updates } = action.payload;
      if (!state.returnRequests[fulfillmentGroupId]) {
        state.returnRequests[fulfillmentGroupId] = {
          selectedItems: {},
          reason: '',
          comments: '',
        };
      }
      Object.assign(state.returnRequests[fulfillmentGroupId], updates);
    },
    
    clearReturnRequest: (state, action) => {
      const { fulfillmentGroupId } = action.payload;
      if (fulfillmentGroupId) {
        delete state.returnRequests[fulfillmentGroupId];
      } else {
        state.returnRequests = {};
      }
    },
    
    // Tracking information
    setTrackingLoading: (state, action) => {
      state.tracking.isLoading = action.payload;
      if (action.payload) {
        state.tracking.error = null;
      }
    },
    
    setTrackingData: (state, action) => {
      state.tracking.data = action.payload;
      state.tracking.isLoading = false;
      state.tracking.error = null;
    },
    
    setTrackingError: (state, action) => {
      state.tracking.error = action.payload;
      state.tracking.isLoading = false;
    },
    
    clearTracking: (state) => {
      state.tracking = {
        isLoading: false,
        error: null,
        data: null,
      };
    },
    
    // Reset entire state
    resetFulfillmentGroupState: () => initialState,
  },
});

export const {
  openModal,
  closeModal,
  clearModal,
  clearAllModals,
  setLoadingState,
  clearLoadingState,
  toggleItemSelection,
  selectAllItems,
  clearItemSelection,
  setPickupVerifying,
  setPickupError,
  clearPickupError,
  updateReturnRequest,
  clearReturnRequest,
  setTrackingLoading,
  setTrackingData,
  setTrackingError,
  clearTracking,
  resetFulfillmentGroupState,
} = fulfillmentGroupSlice.actions;

// Selectors
export const selectActiveModals = (state) => state.fulfillmentGroup.activeModals;
export const selectModalById = (modalId) => (state) => state.fulfillmentGroup.activeModals[modalId];
export const selectLoadingState = (key) => (state) => state.fulfillmentGroup.loadingStates[key] || false;
export const selectSelectedItems = (fulfillmentGroupId) => (state) => 
  state.fulfillmentGroup.selectedItems[fulfillmentGroupId] || {};
export const selectPickupVerification = (state) => state.fulfillmentGroup.pickupVerification;
export const selectReturnRequest = (fulfillmentGroupId) => (state) => 
  state.fulfillmentGroup.returnRequests[fulfillmentGroupId] || { selectedItems: {}, reason: '', comments: '' };
export const selectTracking = (state) => state.fulfillmentGroup.tracking;

export default fulfillmentGroupSlice.reducer; 